<template>
<div class="rf-gantt">
	<div class="rf-gantt-scroll" v-if="projects.length">
		<div class="rf-gantt-inner" :style="{ minWidth: (LEFT_WIDTH + c_timelineWidth) + 'px' }">
			<!-- Year header -->
			<div class="rf-gantt-row rf-gantt-row-hdr">
				<div class="rf-gantt-left rf-gantt-left-spacer"></div>
				<div class="rf-gantt-right" :style="{ width: c_timelineWidth + 'px' }">
					<div class="rf-gantt-timeline-years">
						<div
							class="rf-gantt-year-cell"
							v-for="yr in c_yearGroups"
							:key="yr.year"
							:style="{ width: yr.monthCount * MONTH_WIDTH + 'px' }"
						>{{ yr.year }}</div>
					</div>
				</div>
			</div>

			<!-- Quarter header -->
			<div class="rf-gantt-row rf-gantt-row-hdr">
				<div class="rf-gantt-left rf-gantt-left-spacer"></div>
				<div class="rf-gantt-right" :style="{ width: c_timelineWidth + 'px' }">
					<div class="rf-gantt-timeline-quarters">
						<div
							class="rf-gantt-quarter-cell"
							v-for="qtr in c_quarterGroups"
							:key="qtr.key"
							:style="{ width: qtr.monthCount * MONTH_WIDTH + 'px' }"
						>{{ qtr.label }}</div>
					</div>
				</div>
			</div>

			<!-- Month / column header -->
			<div class="rf-gantt-row rf-gantt-row-hdr">
				<div class="rf-gantt-left">
					<div class="rf-gantt-col rf-gantt-col-name">Project Name</div>
					<div class="rf-gantt-col rf-gantt-col-date">Start Date</div>
					<div class="rf-gantt-col rf-gantt-col-stage">Stage</div>
				</div>
				<div class="rf-gantt-right" :style="{ width: c_timelineWidth + 'px' }">
					<div class="rf-gantt-timeline-months">
						<div
							class="rf-gantt-month-cell"
							v-for="mo in c_timelineMonths"
							:key="mo.key"
						>{{ mo.label }}</div>
					</div>
				</div>
			</div>

			<!-- Data rows -->
			<div
				class="rf-gantt-row rf-gantt-row-body"
				v-for="(item, ix) in projects"
				:key="item.id"
				:class="{ 'rf-gantt-row-alt': ix % 2 === 1 }"
			>
				<div class="rf-gantt-left">
					<div class="rf-gantt-col rf-gantt-col-name" :title="item.name">{{ item.name }}</div>
					<div class="rf-gantt-col rf-gantt-col-date">{{ m_fmtDate(item.startDate) }}</div>
					<div class="rf-gantt-col rf-gantt-col-stage">{{ item.stage }}</div>
				</div>
				<div class="rf-gantt-right rf-gantt-timeline-row" :style="{ width: c_timelineWidth + 'px' }">
					<div
						class="rf-gantt-month-bar"
						v-for="(mo, mix) in c_timelineMonths"
						:key="mo.key"
						v-show="m_isActiveInMonth(item, mo)"
						:style="{ left: mix * MONTH_WIDTH + 'px', width: MONTH_WIDTH + 'px' }"
						:class="{
                                'rf-gantt-bar-awarded': item.type === 'project',
                                'rf-gantt-bar-onHold': item.statusName === 'On Hold',
                                'rf-gantt-bar-pipeline': item.type !== 'project' && item.statusName !== 'On Hold'
                               }"
					>{{ m_fmtMoney(m_getMonthRevenue(item, mo)) }}</div>
				</div>
			</div>

			<!-- Footer -->
			<div class="rf-gantt-row rf-gantt-row-ftr">
				<div class="rf-gantt-left">
					<div class="rf-gantt-col rf-gantt-col-name">Total</div>
					<div class="rf-gantt-col rf-gantt-col-date"></div>
					<div class="rf-gantt-col rf-gantt-col-stage"></div>
				</div>
				<div class="rf-gantt-right" :style="{ width: c_timelineWidth + 'px' }">
					<div class="rf-gantt-timeline-footer">
						<div
							class="rf-gantt-quarter-total"
							v-for="qtr in c_quarterGroups"
							:key="qtr.key"
							:style="{ width: qtr.monthCount * MONTH_WIDTH + 'px' }"
						>{{ c_quarterTotals[qtr.key] || '' }}</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<p class="rf-gantt-nodata" v-else>No data matches the current filters.</p>
</div>
</template>

<script>
import util from "@/utils/util.js";
import {
	buildProjectRevenueMap,
	monthKeyFromGanttMonth,
	fn_forecastStartDateFull,
	REVENUE_VIEW_CONTRACT,
	STAGE_AWARDED,
} from "@/utils/forecastUtils.js";

const MONTH_WIDTH = 80;
const LEFT_WIDTH = 320;

function toFirstOfMonth(d) {
	return new Date(d.getFullYear(), d.getMonth(), 1);
}

function toLastOfMonth(d) {
	return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function parseDate(str) {
	if (!str) return null;
	return new Date(str + "T12:00:00");
}

export default {
	name: "kt-revenue-forecast-gantt",
	props: {
		projects: {
			type: Array,
			required: true,
			default:[]
		},
		revenueView: {
			type: String,
			default: REVENUE_VIEW_CONTRACT,
		},
		fromDate: {
			type: String,
			required: true,
		},
		toDate: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			STAGE_AWARDED,
			MONTH_WIDTH,
			LEFT_WIDTH,
		};
	},
	computed: {
		c_revenueMaps() {
			const maps = {};
			(this.projects || []).forEach(p => {
				maps[p.id] = buildProjectRevenueMap(p, this.revenueView);
			});
			return maps;
		},
		c_timelineMonths() {
			const months = [];
			const from = parseDate(this.fromDate);
			const to = parseDate(this.toDate);
			if (!from || !to || isNaN(from) || isNaN(to)) return months;

			const minDate = toFirstOfMonth(from);
			const maxDate = toLastOfMonth(to);
			const current = new Date(minDate.getFullYear(), minDate.getMonth(), 1);

			while (current <= maxDate) {
				const yr = current.getFullYear();
				const mo = current.getMonth();
				months.push({
					year: yr,
					quarter: Math.floor(mo / 3) + 1,
					month: mo,
					label: current.toLocaleString("default", { month: "short" }),
					key: `${yr}-${mo}`,
				});
				current.setMonth(current.getMonth() + 1);
			}
			return months;
		},
		c_timelineWidth() {
			return Math.max(this.c_timelineMonths?.length * MONTH_WIDTH, MONTH_WIDTH);
		},
		c_yearGroups() {
			const groups = [];
			this.c_timelineMonths?.forEach(mo => {
				let grp = groups.find(g => g.year === mo.year);
				if (!grp) {
					grp = { year: mo.year, monthCount: 0 };
					groups.push(grp);
				}
				grp.monthCount++;
			});
			return groups;
		},
		c_quarterGroups() {
			const groups = [];
			this.c_timelineMonths?.forEach(mo => {
				const key = `${mo.year}-Q${mo.quarter}`;
				let grp = groups.find(g => g.key === key);
				if (!grp) {
					grp = { key, label: `Q${mo.quarter}`, year: mo.year, quarter: mo.quarter, monthCount: 0 };
					groups.push(grp);
				}
				grp.monthCount++;
			});
			return groups;
		},
		c_quarterTotals() {
			const totals = {};
			this.c_quarterGroups?.forEach(qtr => {
				let sum = 0;
				const qtrMonths = this.c_timelineMonths?.filter(
					mo => mo.year === qtr.year && mo.quarter === qtr.quarter
				);
				this.projects?.forEach(item => {
					qtrMonths?.forEach(mo => {
						sum += this.m_getMonthRevenue(item, mo);
					});
				});
				totals[qtr.key] = sum ? util.fn_fmtMoney(sum, true) : "";
			});
			return totals;
		},
	},
	methods: {
		m_getMonthRevenue(item, mo) {
			const monthKey = monthKeyFromGanttMonth(mo);
			const map = this.c_revenueMaps[item.id];
			return map && map[monthKey] != null ? map[monthKey] : 0;
		},
		m_fmtMoney(val) {
		const n = Number(val);
		if (isNaN(n)) return null; 
		return util.fn_fmtMoney(Math.round((n + Number.EPSILON) * 100) / 100, true);
		},
		m_fmtDate(dt) {
			if (!dt) return '—';
			const d = new Date(dt + (dt.length <= 10 ? 'T12:00:00' : ''));
			if (isNaN(d)) return '—';
			const mm = String(d.getMonth() + 1).padStart(2, '0');
			const dd = String(d.getDate()).padStart(2, '0');
			return `${mm}-${dd}-${d.getFullYear()}`;
		},
		m_itemStartDate(item) {
			return fn_forecastStartDateFull(item);
		},
		m_isActiveInMonth(item, mo) {
			return this.m_getMonthRevenue(item, mo) > 0;
		},
	},
};
</script>

<style scoped>
.rf-gantt {
	width: 100%;
	font-size: 12px;
	font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
	--rf-hdr-h: 28px;
	--rf-body-h: 32px;
	--rf-ftr-h: 32px;
	--rf-left-w: 320px;
}

.rf-gantt-scroll {
	width: 100%;
	overflow-x: auto;
	border: 1px solid #c8d6e8;
	border-radius: 4px;
}

.rf-gantt-inner {
	display: flex;
	flex-direction: column;
}

/* Each row holds left + right together for perfect alignment */
.rf-gantt-row {
	box-sizing: border-box;
	display: flex;
	flex-direction: row;
	align-items: stretch;
	border-bottom: 1px solid #e8edf5;
}

.rf-gantt-row-hdr {
	height: var(--rf-hdr-h);
	background: #b8d4f0;
	border-bottom-color: #9bb8d8;
}

.rf-gantt-row-body {
	height: var(--rf-body-h);
	background: #f0f4fa;
}

.rf-gantt-row-body.rf-gantt-row-alt {
	background: #e8eef8;
}

.rf-gantt-row-ftr {
	height: var(--rf-ftr-h);
	background: #b8d4f0;
	border-bottom: none;
}

.rf-gantt-left {
	box-sizing: border-box;
	display: flex;
	flex-shrink: 0;
	width: var(--rf-left-w);
	position: sticky;
	left: 0;
	z-index: 2;
	background: inherit;
	box-shadow: 2px 0 4px rgba(0, 0, 0, 0.04);
}

.rf-gantt-left-spacer {
	background: #b8d4f0;
}

.rf-gantt-right {
	box-sizing: border-box;
	flex-shrink: 0;
}

.rf-gantt-col {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	padding: 0 8px;
	border-right: 1px solid #c8d6e8;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	height: 100%;
}

.rf-gantt-col-name { width: 140px; flex-shrink: 0; }
.rf-gantt-col-date { width: 100px; flex-shrink: 0; }
.rf-gantt-col-stage { width: 80px; flex-shrink: 0; border-right: none; }

.rf-gantt-row-hdr .rf-gantt-col {
	font-weight: 600;
	justify-content: center;
}

.rf-gantt-row-ftr .rf-gantt-col {
	font-weight: 600;
}

.rf-gantt-timeline-years,
.rf-gantt-timeline-quarters,
.rf-gantt-timeline-months,
.rf-gantt-timeline-footer {
	display: flex;
	height: 100%;
}

.rf-gantt-year-cell,
.rf-gantt-quarter-cell {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
	border-right: 2px solid #9bb8d8;
	flex-shrink: 0;
	height: 100%;
}

.rf-gantt-month-cell {
	box-sizing: border-box;
	width: 80px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
	border-right: 1px solid #c8d6e8;
	height: 100%;
}

.rf-gantt-timeline-row {
	position: relative;
}

.rf-gantt-month-bar {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	height: 22px;
	border-radius: 11px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #fff;
	font-size: 10px;
	font-weight: 600;
	overflow: hidden;
}

.rf-gantt-bar-pipeline {
	background: #4caf50;
}

.rf-gantt-bar-awarded {
	background: #4076ba;
}

.rf-gantt-quarter-total {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
	border-right: 2px solid #9bb8d8;
	flex-shrink: 0;
	height: 100%;
}

.rf-gantt-nodata {
	text-align: center;
	padding: 2em;
	color: #6c757d;
	font-size: 0.9em;
}
.rf-gantt-bar-onHold {
	background: #fd7e14;
}
</style>
